/**
 * Gas fees for self-custody transactions sent through the wallet.
 *
 * Polygon Amoy nodes reject a priority fee ("tip") below 25 gwei. When the dapp sends no fee,
 * MetaMask mobile falls back to a 1.5 gwei tip on Amoy, the node refuses the transaction
 * ("gas tip cap 1500000000, minimum needed 25000000000") and the wallet reports it to the app
 * as code 4001 "User rejected the request" although the user confirmed it. Sending the fees
 * ourselves avoids that. Polygon mainnet is left to the wallet as before.
 */
import { Config, estimateFeesPerGas, getAccount, getChainId } from '@wagmi/core';
import { parseGwei } from 'viem';

export const POLYGON_AMOY_CHAIN_ID = 80002;

/** Amoy's minimum is 25 gwei; 30 keeps headroom if the network raises it. */
export const AMOY_MIN_PRIORITY_FEE_WEI = parseGwei('30');

export interface WalletFeeOverrides {
    maxFeePerGas?: bigint;
    maxPriorityFeePerGas?: bigint;
}

export interface FeeEstimate {
    maxFeePerGas: bigint;
    maxPriorityFeePerGas: bigint;
}

/**
 * Raises the tip to the minimum and keeps the estimate's base-fee headroom on top of it, so
 * maxFeePerGas always covers the base fee plus the raised tip.
 */
export function applyMinimumPriorityFee(estimate: FeeEstimate, minPriorityFee: bigint): Required<WalletFeeOverrides> {
    const tip = estimate.maxPriorityFeePerGas > minPriorityFee ? estimate.maxPriorityFeePerGas : minPriorityFee;
    const baseFeeHeadroom = estimate.maxFeePerGas > estimate.maxPriorityFeePerGas
        ? estimate.maxFeePerGas - estimate.maxPriorityFeePerGas
        : 0n;
    return { maxFeePerGas: baseFeeHeadroom + tip, maxPriorityFeePerGas: tip };
}

export interface WalletFeeDeps {
    chainId: (config: Config) => number | undefined;
    estimate: (config: Config, chainId: number) => Promise<FeeEstimate>;
}

const defaultDeps: WalletFeeDeps = {
    chainId: (config) => getAccount(config).chainId ?? getChainId(config),
    estimate: (config, chainId) => estimateFeesPerGas(config, { chainId }),
};

/**
 * Fee fields to spread into writeContract / sendTransaction. Empty (wallet decides, as before)
 * on any chain other than Amoy, or when the fee estimate cannot be read.
 */
export async function walletFeeOverrides(config: Config, deps: WalletFeeDeps = defaultDeps): Promise<WalletFeeOverrides> {
    const chainId = deps.chainId(config);
    if (chainId !== POLYGON_AMOY_CHAIN_ID) {
        return {};
    }
    try {
        const estimate = await deps.estimate(config, chainId);
        return applyMinimumPriorityFee(estimate, AMOY_MIN_PRIORITY_FEE_WEI);
    } catch {
        return {};
    }
}
